import { Component, Input } from '@angular/core';
import {
    DO_NOTHING,
    FlightLogCollectionService,
    FlightLogCollectionSourceDto,
    FlightLogCollectionSourceWithApiKeyDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { FlightLogSettingsService } from './flight-log-settings.service';

@Component({
    selector: 'drone-harmony-settings',
    template: ` <div *ngIf="newSource != null" class="well">
            <p>
                To complete the setup, enter the FlyFreely API key into your Drone Harmony settings.
            </p>
            <strong
                >{{ newSource.apiKey.id }}.{{ newSource.apiKey.apiKey }}</strong
            ><copy-to-clipboard
                [content]="newSource.apiKey.id + '.' + newSource.apiKey.apiKey"
            ></copy-to-clipboard>
            <p>
                Make a note of this key as it can not be retrieved again once
                this window is closed. A new key will need to be generated.
            </p>
        </div>
        <ng-container
            *ngIf="droneHarmonyList$ | async as droneHarmonyList"
            class="vertical-container fill"
        >
            <div
                class="bottom-buffer fill"
                *ngIf="droneHarmonyList.length === 0"
            >
                <empty
                    componentName="empty-drone-harmony-accounts"
                    style="margin-left: 30px;"
                >
                    <button
                        type="button"
                        class="btn btn-default top-buffer"
                        (click)="setupDroneHarmony()"
                    >
                        Setup Drone Harmony
                    </button>
                </empty>
            </div>

            <table *ngIf="droneHarmonyList.length > 0" class="table">
                <thead>
                    <tr>
                        <th style="width: 70%">Last Completion Time</th>
                        <th
                            *ngIf="flightLogSettingsService.canUseAlerts"
                            style="width: 10%"
                        >
                            Alerts
                        </th>
                        <th
                            style="width: 10%"
                            *ngIf="hasMissionConfig && canManageAll"
                        >
                            Automatic Mission Creation
                        </th>
                        <th style="width: 10%"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let account of droneHarmonyList">
                        <td>
                            {{ account.lastCompletionTime | formatDateTime }}
                        </td>
                        <td
                            *ngIf="
                                flightLogSettingsService.canUseAlerts &&
                                canManageAll
                            "
                        >
                            <flight-log-alert
                                [alertSettings]="account.alertSettings"
                                (showSettings)="
                                    flightLogSettingsService.showAlertSettings(
                                        account
                                    )
                                "
                            ></flight-log-alert>
                        </td>
                        <td *ngIf="hasMissionConfig && canManageAll">
                            <automatic-mission-assignment-settings
                                [entry]="account"
                            ></automatic-mission-assignment-settings>
                        </td>
                        <td class="actions">
                            <button
                                class="btn btn-tertiary btn-delete"
                                type="button"
                                (click)="deleteDroneDeploy(account)"
                                [disabled]="working"
                                tooltip="Delete"
                                placement="left"
                            >
                                <span class="fal fa-trash-alt"></span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </ng-container>`
})
export class DroneHarmonySettings {
    @Input() hasMissionConfig = false;
    @Input() canManageAll = false;

    private ngUnsubscribe$ = new Subject<void>();

    droneHarmonyList$: Observable<FlightLogCollectionSourceDto[]>;

    newSource?: FlightLogCollectionSourceWithApiKeyDto;

    working: boolean;

    constructor(
        public flightLogSettingsService: FlightLogSettingsService,
        private workTracker: WorkTracker,
        private commonDialoguesService: CommonDialoguesService,
        private flightLogCollectionService: FlightLogCollectionService,
        private logging: FlyFreelyLoggingService
    ) {
        this.droneHarmonyList$ =
            this.flightLogSettingsService.flightLogCollectionSourceList$.pipe(
                takeUntil(this.ngUnsubscribe$),
                map(sources =>
                    sources.filter(
                        s =>
                            s.type ===
                            FlightLogCollectionSourceDto.Type.DRONE_HARMONY
                    )
                )
            );
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setupDroneHarmony() {
        this.flightLogCollectionService
            .createApiSource({
                organisationId: this.flightLogSettingsService.organisationId,
                type: 'DRONE_HARMONY'
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.newSource = result;
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error creating Drone Harmony source ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    deleteDroneDeploy(entry: FlightLogCollectionSourceDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Collection',
                'Are you sure you want to delete this collection?',
                'Delete',
                () => Promise.resolve()
            )
            .then(() =>
                this.flightLogCollectionService
                    .deleteSource(entry.id)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(() =>
                        this.logging.success('Drone Harmony account deleted')
                    )
                    .add(this.workTracker.createTracker())
            )
            .catch(DO_NOTHING);
    }
}
